import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MindMapDocument } from '@/model/document';
import { parseFreeMind } from '@/import/parseFreeMind';
import { parseIndentedText } from '@/import/parseIndentedText';
import { parseJson } from '@/import/parseJson';
import { applyImport, applySnapshot } from '@/import/applyImport';
import { generateId } from '@/utils/id';
import { db } from '@/persistence/indexedDb';
import { useIsMobile } from '@/hooks/useMediaQuery';

interface ImportDialogProps {
  onImported: (mapDoc: MindMapDocument, mapId: string) => void;
}

export function ImportDialog({ onImported }: ImportDialogProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = useCallback(() => {
    setError(null);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setError(null);
  }, []);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose],
  );

  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result as string;
        try {
          importFile(file.name, content, onImported);
          setIsOpen(false);
          setError(null);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Unknown import error';
          setError(message);
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
      };
      reader.readAsText(file);

      // Reset file input so the same file can be re-imported
      e.target.value = '';
    },
    [onImported],
  );

  return (
    <>
      <button
        onClick={handleButtonClick}
        title={t('dialogs.import')}
        style={{
          background: 'var(--bg-surface-raised)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-strong)',
          borderRadius: 6,
          padding: '6px 12px',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontFamily: 'var(--font-family)',
        }}
      >
        {t('dialogs.import')}
      </button>

      {isOpen && (
        <div
          onClick={handleOverlayClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--bg-overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              background: 'var(--bg-surface)',
              border: isMobile ? 'none' : '1px solid var(--border-default)',
              borderRadius: isMobile ? 0 : 'var(--radius-lg)',
              padding: 24,
              minWidth: isMobile ? undefined : 360,
              maxWidth: isMobile ? undefined : 480,
              width: isMobile ? '100vw' : undefined,
              height: isMobile ? '100vh' : undefined,
              margin: isMobile ? 0 : undefined,
              boxShadow: isMobile ? 'none' : 'var(--shadow-lg)',
              fontFamily: 'var(--font-family)',
              boxSizing: 'border-box',
            }}
          >
            <h2
              style={{
                color: 'var(--text-primary)',
                fontSize: 'var(--font-size-xl)',
                fontWeight: 600,
                margin: '0 0 16px 0',
              }}
            >
              {t('dialogs.importTitle')}
            </h2>

            <p
              style={{
                color: 'var(--text-secondary)',
                fontSize: 'var(--font-size-sm)',
                margin: '0 0 16px 0',
                lineHeight: 1.5,
              }}
            >
              {t('dialogs.importDescription')}
            </p>

            <ul
              style={{
                color: 'var(--text-secondary)',
                fontSize: 'var(--font-size-sm)',
                margin: '0 0 20px 0',
                paddingLeft: 20,
                lineHeight: 1.8,
              }}
            >
              <li><strong style={{ color: '#cbd5e1' }}>.mm</strong> - FreeMind XML</li>
              <li><strong style={{ color: '#cbd5e1' }}>.txt / .md</strong> - Indented text outline</li>
              <li><strong style={{ color: '#cbd5e1' }}>.json</strong> - Ideatwig native format</li>
            </ul>

            {error && (
              <div
                style={{
                  background: 'var(--bg-error)',
                  border: '1px solid var(--bg-error)',
                  borderRadius: 6,
                  padding: '8px 12px',
                  marginBottom: 16,
                  color: '#fecaca',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={handleClose}
                style={{
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 6,
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-family)',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleFileSelect}
                style={{
                  background: 'var(--color-primary)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  fontFamily: 'var(--font-family)',
                }}
              >
                {t('dialogs.chooseFile')}
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".mm,.txt,.json,.md"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}
    </>
  );
}

function importFile(
  filename: string,
  content: string,
  onImported: (mapDoc: MindMapDocument, mapId: string) => void,
): void {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  const mapDoc = new MindMapDocument();
  const mapId = generateId();

  if (ext === 'json') {
    const snapshot = parseJson(content);
    applySnapshot(mapDoc, snapshot);
  } else if (ext === 'mm') {
    const tree = parseFreeMind(content);
    applyImport(mapDoc, tree);
  } else if (ext === 'txt' || ext === 'md') {
    const tree = parseIndentedText(content);
    applyImport(mapDoc, tree);
  } else {
    throw new Error(`Unsupported file format: .${ext}`);
  }

  // Save to IndexedDB immediately
  const state = mapDoc.encodeState();
  const title = mapDoc.getMeta().title;
  // Fire-and-forget save via the persistence layer
  void db.saveMap(mapId, title, state);

  onImported(mapDoc, mapId);
}
